import { useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';

import { CustomerTripOrderStatus, generateApolloClientHeaders, GRAPHQL_PAYLOAD_CONST } from '../../../modules';
import { phoenixAccountGetUpcomingTripsBasicInfo } from '../../../modules/graph';
import {
  phoenixAccountDttGetLoyaltyCurrencies,
  phoenixAccountDttGetMyActivityRewardsEarned,
  phoenixAccountDttGetUpcomingCustomerTrips,
} from '../../../modules/graphV2';
import { updateImageDomain } from '../../../modules/utils/imageUtility';
import { getHotelImageUrl } from '../../EarningsSummary/earningsSummaryHelper';

import { WalletNuaItem, WalletFnaItem, WalletNuaExpiryGroup, WalletNightlyUpgradeAwardsSectionStatus } from '../types';

export const getNuaAvailableCount = (items: WalletNuaItem[]): number =>
  items.filter(item => item.status === NUA_ITEM_STATUS.AVAILABLE).reduce((sum, item) => sum + (item.count || 0), 0);

export const getNuaTotalCount = (items: WalletNuaItem[]): number =>
  items.reduce((sum, item) => sum + (item.count || 0), 0);

type CertEdge = {
  node?: {
    awardType?: { code?: string | null; description?: string | null; label?: string | null } | null;
    expirationDate?: string | null;
    numberOfNights?: number | null;
    isCancellable?: boolean | null;
  } | null;
};

type FnaReservationDetail = {
  propertyId?: string | null;
  arrivalDate?: string | null;
  certificateNumber?: number | null;
  certificateSource?: string | null;
  confirmationNumber?: string | null;
  statusType?: string | null;
  expirationDate?: string | null;
};

type FnaAvailableInfo = {
  certificateNumber?: number | null;
  expirationDate?: string | null;
};

export function buildFnaExpirationByCertificateNumber(availableInfo?: FnaAvailableInfo[] | null): Map<number, string> {
  const map = new Map<number, string>();

  (availableInfo ?? []).forEach(info => {
    if (info?.certificateNumber != null && info.expirationDate) {
      map.set(info.certificateNumber, info.expirationDate);
    }
  });

  return map;
}

export function resolveAppliedFnaExpirationDate(
  reservation?: FnaReservationDetail | null,
  expirationByCertificateNumber?: Map<number, string>
): string | undefined {
  if (reservation?.expirationDate) {
    return reservation.expirationDate;
  }

  const certificateNumber = reservation?.certificateNumber;
  if (certificateNumber != null && expirationByCertificateNumber) {
    return expirationByCertificateNumber.get(certificateNumber);
  }

  return undefined;
}

type TripEdge = {
  node?: {
    items?: Array<{
      id?: string;
      basicInformation?: {
        startDate?: string | null;
        endDate?: string | null;
        confirmationNumber?: string | null;
      } | null;
      property?: { id?: string | null } | null;
      nuaInformation?: {
        nuaRequestStatus?: {
          status?: string | null;
          nuaTokensAttached?: number | null;
        } | null;
      } | null;
    }> | null;
  } | null;
};

const NUA_STATUS_APPROVED = ['APPROVED', 'CONFIRMED', 'GRANTED', 'GUARANTEED'];
const NUA_STATUS_PENDING = ['REQUESTED', 'PENDING', 'REQUEST_PENDING', 'REQUESTPENDING'];

const NUA_ITEM_STATUS = {
  AVAILABLE: 'available',
  APPROVED: 'approved',
  REQUEST_PENDING: 'requestPending',
} as const;

function isFutureDate(date?: string | null): boolean {
  if (!date) return false;
  const t = new Date(date).getTime();
  if (Number.isNaN(t)) return false;
  return t >= new Date().setHours(0, 0, 0, 0);
}

function isAwardExpired(date?: string | null): boolean {
  if (!date) return false;
  const t = new Date(date).getTime();
  if (Number.isNaN(t)) return false;
  return t < new Date().setHours(0, 0, 0, 0);
}

type NuaAvailableDetail = { count?: number; expirationDate?: string };

export function getActiveNuaAvailableSummary(
  available?: {
    count?: number;
    details?: NuaAvailableDetail[];
  } | null
): { count: number; expirationDate?: string; expiryGroups: WalletNuaExpiryGroup[] } {
  const details = available?.details ?? [];
  const activeDetails = details.filter(detail => !isAwardExpired(detail.expirationDate));

  if (activeDetails.length > 0) {
    const count = activeDetails.reduce((sum, detail) => sum + (detail.count ?? 0), 0);
    const mergedByDate = new Map<string, number>();

    activeDetails
      .filter((d): d is { count: number; expirationDate: string } => !!d.expirationDate && (d.count ?? 0) > 0)
      .forEach(detail => {
        mergedByDate.set(detail.expirationDate, (mergedByDate.get(detail.expirationDate) ?? 0) + detail.count);
      });

    const sortedGroups: WalletNuaExpiryGroup[] = Array.from(mergedByDate.entries())
      .map(([expirationDate, groupCount]) => ({ expirationDate, count: groupCount }))
      .sort((a, b) => new Date(a.expirationDate).getTime() - new Date(b.expirationDate).getTime());

    const expirationDate = sortedGroups[0]?.expirationDate;
    return { count, expirationDate, expiryGroups: sortedGroups };
  }

  const aggregateCount = available?.count ?? 0;
  if (details.length === 0 && aggregateCount > 0) {
    return { count: aggregateCount, expiryGroups: [] };
  }

  return { count: 0, expiryGroups: [] };
}

export function resolveNuaAvailableSource(
  statusInfo: NuaStatusInfoEntry[],
  rewardsAvailable?: { count?: number; details?: NuaAvailableDetail[] } | null
): { count?: number; details?: NuaAvailableDetail[] } | undefined {
  const availableStatusEntry = statusInfo.find(entry => entry?.statusType?.toUpperCase() === 'AVAILABLE');

  if (availableStatusEntry) {
    return { count: availableStatusEntry.count, details: availableStatusEntry.expirationDetails };
  }

  return rewardsAvailable ?? undefined;
}

/** One available tile per unique expiry date (WEB-206495). Same date aggregates into one card. */
export function buildAvailableNuaItemsFromGroups(
  expiryGroups: WalletNuaExpiryGroup[],
  aggregateCount = 0,
  soonestExpiration?: string
): WalletNuaItem[] {
  if (expiryGroups.length > 0) {
    return expiryGroups.map(group => ({
      id: `nua-available-${group.expirationDate}`,
      count: group.count,
      expirationDate: group.expirationDate,
      expiryGroups: [group],
      status: WalletNightlyUpgradeAwardsSectionStatus.Available,
    }));
  }

  if (aggregateCount > 0) {
    return [
      {
        id: 'nua-available',
        count: aggregateCount,
        expirationDate: soonestExpiration,
        status: WalletNightlyUpgradeAwardsSectionStatus.Available,
      },
    ];
  }

  return [];
}

function classifyNuaStatus(raw?: string | null): WalletNuaItem['status'] | null {
  if (!raw) return null;
  const norm = raw.replace(/\s+/g, '').toUpperCase();
  if (NUA_STATUS_APPROVED.includes(norm)) return WalletNightlyUpgradeAwardsSectionStatus.Approved;
  if (NUA_STATUS_PENDING.includes(norm)) return WalletNightlyUpgradeAwardsSectionStatus.RequestPending;
  return null;
}

type PropertyBasic = {
  id?: string;
  basicInformation?: { name?: string | null; brand?: { id?: string | null } | null } | null;
  media?: {
    primaryImage?: {
      edges?: Array<{
        node?: { imageUrls?: { square?: string | null; wideHorizontal?: string | null } | null } | null;
      }> | null;
    } | null;
    photoGallery?: {
      hotelView?: {
        edges?: Array<{
          node?: { imageUrls?: { square?: string | null; wideHorizontal?: string | null } | null } | null;
        }> | null;
      } | null;
    } | null;
  } | null;
};

function pickPropertyImage(p?: PropertyBasic, _locale = 'en-US'): { src?: string; alt?: string } {
  const src = getHotelImageUrl(p, _locale);
  return { src, alt: p?.basicInformation?.name || undefined };
}

type UseWalletAwardsPageContext = {
  sessionData?: {
    cacheData?: {
      data?: {
        consumerID?: string;
        memberLevel?: string;
      };
    };
  };
  headersData?: Record<string, string | undefined>;
};

type UseWalletAwardsArgs = {
  pageContext: UseWalletAwardsPageContext;
  isLocal: boolean;
  currentLocale?: string;
  skip?: boolean;
};

type NuaStatusInfoEntry = { count?: number; statusType?: string; expirationDetails?: NuaAvailableDetail[] };

export function useWalletAwards({ pageContext, isLocal, currentLocale = 'en-US', skip = false }: UseWalletAwardsArgs) {
  const sessionData = pageContext?.sessionData?.cacheData?.data;
  const customerId = sessionData?.consumerID;

  const shouldSkip = skip || !customerId;

  const {
    data: rewardsData,
    loading: rewardsLoading,
    error: rewardsError,
  } = useQuery(phoenixAccountDttGetMyActivityRewardsEarned, {
    variables: { customerId },
    context: generateApolloClientHeaders(isLocal, pageContext),
    fetchPolicy: 'no-cache',
    skip: shouldSkip,
    errorPolicy: 'all',
  });

  const {
    data: currenciesData,
    loading: currenciesLoading,
    error: currenciesError,
  } = useQuery(phoenixAccountDttGetLoyaltyCurrencies, {
    variables: { customerId },
    context: generateApolloClientHeaders(isLocal, pageContext),
    fetchPolicy: 'no-cache',
    skip: shouldSkip,
    errorPolicy: 'all',
  });

  const {
    data: tripsData,
    loading: tripsLoading,
    error: _tripsError,
  } = useQuery(phoenixAccountDttGetUpcomingCustomerTrips, {
    variables: {
      customerId,
      status: CustomerTripOrderStatus.active,
      limit: 25,
      offset: 0,
      source: GRAPHQL_PAYLOAD_CONST.source,
    },
    context: generateApolloClientHeaders(isLocal, pageContext),
    fetchPolicy: 'no-cache',
    skip: shouldSkip,
    errorPolicy: 'all',
  });

  const [fetchBasicInfo, { data: basicInfoData, loading: basicInfoLoading, error: _basicInfoError }] = useLazyQuery(
    phoenixAccountGetUpcomingTripsBasicInfo
  );

  const upcomingOrderItems = useMemo(() => {
    const edges: TripEdge[] = (tripsData?.customer?.ordersByStatus?.edges as TripEdge[]) ?? [];
    return edges.flatMap(e => e?.node?.items ?? []).filter(Boolean);
  }, [tripsData]);

  const fnaReservationDetails = useMemo<FnaReservationDetail[]>(() => {
    return currenciesData?.loyalty?.benefits?.currenciesByCustomerId?.fna?.reservationDetails ?? [];
  }, [currenciesData]);

  const fnaExpirationByCertificateNumber = useMemo(
    () =>
      buildFnaExpirationByCertificateNumber(
        currenciesData?.loyalty?.benefits?.currenciesByCustomerId?.fna?.availableInfo
      ),
    [currenciesData]
  );

  const propertyIdsNeeded = useMemo(() => {
    const ids = new Set<string>();
    // NUA approved/pending tiles
    upcomingOrderItems.forEach(item => {
      const nuaStatus = classifyNuaStatus(item?.nuaInformation?.nuaRequestStatus?.status);
      if (nuaStatus && item?.property?.id) {
        ids.add(item.property.id);
      }
    });
    // Applied FNA tiles
    fnaReservationDetails.forEach(r => {
      if (r?.propertyId) ids.add(r.propertyId);
    });
    return Array.from(ids);
  }, [upcomingOrderItems, fnaReservationDetails]);

  const [lastIdsKey, setLastIdsKey] = useState<string>('');

  useEffect(() => {
    if (shouldSkip || propertyIdsNeeded.length === 0) return;
    const key = propertyIdsNeeded.slice().sort().join(',');
    if (key === lastIdsKey) return;
    setLastIdsKey(key);
    fetchBasicInfo({
      variables: { ids: propertyIdsNeeded },
      context: generateApolloClientHeaders(isLocal, pageContext),
    });
  }, [shouldSkip, propertyIdsNeeded, lastIdsKey, fetchBasicInfo, isLocal, pageContext]);

  const propertiesById = useMemo<Record<string, PropertyBasic>>(() => {
    const raw: PropertyBasic[] = basicInfoData?.propertiesByIds ?? [];
    const list: PropertyBasic[] = updateImageDomain(raw, currentLocale);
    return list.reduce<Record<string, PropertyBasic>>((acc, p) => {
      if (p?.id) acc[p.id] = p;
      return acc;
    }, {});
  }, [basicInfoData, currentLocale]);

  const fnaItems = useMemo<WalletFnaItem[]>(() => {
    const certEdges: CertEdge[] = rewardsData?.customer?.loyaltyInformation?.certificates?.edges ?? [];

    // Applied tiles -- one per reservation entry from currenciesByCustomerId
    const appliedItems: WalletFnaItem[] = fnaReservationDetails.map((r, idx) => {
      const propertyId = r?.propertyId || undefined;
      const propBasic = propertyId ? propertiesById[propertyId] : undefined;
      const propImage = pickPropertyImage(propBasic, currentLocale);

      const checkInDate = r?.arrivalDate || undefined;
      let checkOutDate: string | undefined;
      if (checkInDate) {
        const nights = 1;
        const d = new Date(checkInDate);
        d.setDate(d.getDate() + nights);
        checkOutDate = d.toISOString().split('T')[0];
      }

      return {
        id: `fna-applied-${r?.certificateNumber ?? idx}`,
        awardDescription: r?.certificateSource || undefined,
        expirationDate: resolveAppliedFnaExpirationDate(r, fnaExpirationByCertificateNumber),
        status: 'applied',
        propertyId,
        brandId: propBasic?.basicInformation?.brand?.id || undefined,
        propertyName: propBasic?.basicInformation?.name || undefined,
        propertyImageSrc: propImage.src,
        propertyImageAltText: propImage.alt,
        checkInDate,
        checkOutDate,
        confirmationNumber: r?.confirmationNumber || undefined,
      };
    });

    const availableItems: WalletFnaItem[] = certEdges.map((edge, idx) => {
      const node = edge?.node;
      return {
        id: `fna-available-${idx}-${node?.awardType?.code ?? 'cert'}`,
        awardCode: node?.awardType?.code || undefined,
        awardDescription: node?.awardType?.description || node?.awardType?.label || undefined,
        expirationDate: node?.expirationDate || undefined,
        status: 'available',
        numberOfNights: node?.numberOfNights ?? undefined,
        isCancellable: node?.isCancellable ?? undefined,
      };
    });

    const sortedAvailable = availableItems
      .filter(item => !isAwardExpired(item.expirationDate))
      .sort((a, b) => {
        const at = a.expirationDate ? new Date(a.expirationDate).getTime() : Number.MAX_SAFE_INTEGER;
        const bt = b.expirationDate ? new Date(b.expirationDate).getTime() : Number.MAX_SAFE_INTEGER;
        return at - bt;
      });

    return [...appliedItems, ...sortedAvailable];
  }, [rewardsData, fnaReservationDetails, fnaExpirationByCertificateNumber, propertiesById, currentLocale]);

  const nuaItems = useMemo<WalletNuaItem[]>(() => {
    const nuaStatusInfo: NuaStatusInfoEntry[] =
      (currenciesData?.['loyalty']?.['benefits']?.['currenciesByCustomerId']?.['nua']?.['statusInfo'] as
        | NuaStatusInfoEntry[]
        | undefined) ?? [];

    const {
      count: availableCount,
      expirationDate: soonestExpiration,
      expiryGroups,
    } = getActiveNuaAvailableSummary(
      resolveNuaAvailableSource(
        nuaStatusInfo,
        rewardsData?.customer?.loyaltyInformation?.nightlyUpgradeAwards?.available
      )
    );

    const items: WalletNuaItem[] = [];

    upcomingOrderItems.forEach((trip, idx) => {
      const statusKind = classifyNuaStatus(trip?.nuaInformation?.nuaRequestStatus?.status);
      if (!statusKind) return;
      const propertyId = trip?.property?.id || undefined;
      const propBasic = propertyId ? propertiesById[propertyId] : undefined;
      const propImage = pickPropertyImage(propBasic, currentLocale);
      const tokens = trip?.nuaInformation?.nuaRequestStatus?.nuaTokensAttached ?? 1;
      items.push({
        id: `nua-${statusKind}-${trip?.id ?? propertyId ?? idx}`,
        count: tokens,
        expirationDate: trip?.basicInformation?.startDate || undefined,
        status: statusKind,
        propertyId,
        brandId: propBasic?.basicInformation?.brand?.id || undefined,
        propertyName: propBasic?.basicInformation?.name || undefined,
        propertyImageSrc: propImage.src,
        propertyImageAltText: propImage.alt,
        checkInDate: trip?.basicInformation?.startDate || undefined,
        checkOutDate: trip?.basicInformation?.endDate || undefined,
        confirmationNumber: trip?.basicInformation?.confirmationNumber || undefined,
      });
    });

    items.push(...buildAvailableNuaItemsFromGroups(expiryGroups, availableCount, soonestExpiration));

    return items.sort((a, b) => {
      const at = a.expirationDate ? new Date(a.expirationDate).getTime() : Number.MAX_SAFE_INTEGER;
      const bt = b.expirationDate ? new Date(b.expirationDate).getTime() : Number.MAX_SAFE_INTEGER;

      // Primary sort: by expiration date (soonest first)
      if (at !== bt) {
        return at - bt;
      }

      // Secondary sort: when expiration dates are equal, available comes first
      const statusOrder = { available: 0, approved: 1, requestPending: 2 };
      return (statusOrder[a.status] ?? 3) - (statusOrder[b.status] ?? 3);
    });
  }, [currenciesData, rewardsData, upcomingOrderItems, propertiesById, currentLocale]);

  return {
    fnaItems,
    nuaItems,
    isLoading: rewardsLoading || currenciesLoading || tripsLoading || basicInfoLoading,
    // Section-level UXL error only when core award inventory cannot load.
    // Trips and basicInfo are enrichment calls; partial data is still useful.
    hasUxlError: Boolean(rewardsError || currenciesError),
    rewardsData,
    tripsData,
  };
}

export { isAwardExpired, isFutureDate };
